import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Mail, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { emailTemplatesAPI } from '../lib/api';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState({}); // { [key]: { subject, body, dirty } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // current key being saved

  useEffect(() => { reload(); }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await emailTemplatesAPI.list();
      const list = res.data || [];
      setTemplates(list);
      const initial = {};
      list.forEach((t) => { initial[t.key] = { subject: t.subject, body: t.body, dirty: false }; });
      setDrafts(initial);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (key, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value, dirty: true },
    }));
  };

  const handleSave = async (key) => {
    const d = drafts[key];
    if (!d) return;
    setSaving(key);
    try {
      await emailTemplatesAPI.update(key, { subject: d.subject, body: d.body });
      setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], dirty: false } }));
      toast.success(`Saved “${templates.find(t => t.key === key)?.name}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = (key) => {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    setDrafts((prev) => ({
      ...prev,
      [key]: { subject: t.subject, body: t.body, dirty: false },
    }));
  };

  const insertVariable = (key, varKey) => {
    const textarea = document.getElementById(`tpl-body-${key}`);
    const token = `{{${varKey}}}`;
    if (textarea && document.activeElement === textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const cur = drafts[key]?.body || '';
      const next = cur.slice(0, start) + token + cur.slice(end);
      updateDraft(key, 'body', next);
      // Restore caret after the inserted token
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + token.length;
      }, 0);
    } else {
      // Append at end of body
      const cur = drafts[key]?.body || '';
      updateDraft(key, 'body', cur + (cur && !cur.endsWith(' ') ? ' ' : '') + token);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="email-templates-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Email Templates</h1>
            <p className="text-gray-500 mt-1">
              Customise the subject line and body of the transactional emails sent to restaurants, students
              and chef-mentors. Use the variable chips below each editor to insert dynamic values.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {templates.map((t) => {
            const d = drafts[t.key] || { subject: '', body: '', dirty: false };
            return (
              <Card key={t.key} data-testid={`email-tpl-card-${t.key}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-ica-navy">
                    <Mail className="w-5 h-5" />
                    {t.name}
                  </CardTitle>
                  <CardDescription>{t.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subject line</Label>
                    <input
                      data-testid={`tpl-subject-${t.key}`}
                      type="text"
                      value={d.subject}
                      onChange={(e) => updateDraft(t.key, 'subject', e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Email body</Label>
                    <textarea
                      id={`tpl-body-${t.key}`}
                      data-testid={`tpl-body-${t.key}`}
                      rows={6}
                      value={d.body}
                      onChange={(e) => updateDraft(t.key, 'body', e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy resize-y font-mono"
                      placeholder="Enter the email body. Use {{variable_name}} to insert dynamic values."
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Newlines are preserved. The branded header, salutation and footer (“This email was sent on behalf of the Institute of Culinary Arts”) are added automatically.
                    </p>
                  </div>

                  {Array.isArray(t.variables) && t.variables.length > 0 && (
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Available variables — click to insert</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {t.variables.map((v) => (
                          <button
                            key={v.key}
                            type="button"
                            data-testid={`tpl-var-${t.key}-${v.key}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertVariable(t.key, v.key)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-ica-gold/10 text-ica-navy hover:bg-ica-gold/30 border border-ica-gold/40 transition-colors"
                            title={`Insert {{${v.key}}}`}
                          >
                            <span className="font-mono">{`{{${v.key}}}`}</span>
                            <span className="text-gray-500">— {v.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end items-center gap-2 pt-2 border-t border-gray-100">
                    {d.dirty && (
                      <span className="text-xs text-amber-600 mr-auto">Unsaved changes</span>
                    )}
                    <Button
                      data-testid={`tpl-reset-${t.key}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReset(t.key)}
                      disabled={!d.dirty}
                      className="text-gray-500 hover:text-ica-navy"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Discard
                    </Button>
                    <Button
                      data-testid={`tpl-save-${t.key}`}
                      size="sm"
                      onClick={() => handleSave(t.key)}
                      disabled={!d.dirty || saving === t.key}
                      
                    >
                      <Save className="w-3.5 h-3.5 mr-1" />
                      {saving === t.key ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default EmailTemplates;
